import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import BlockTitle from 'components/layout/BlockTitle/BlockTitle';
import Loader from 'components/Loader/Loader';
import { PhoneInput, PhoneInputData } from 'components/PhoneInput/PhoneInput';
import withAuth from 'components/with-auth/with-auth';
import config, { Country } from 'config';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import UserService from 'services/user.service';
import { setUser } from 'store/actions/user.actions';
import { UserType } from 'types/user.types';
import handleAPIValidationErrors from 'utils/api-validation-errors';
import { useNotification } from 'utils/snackbar';

import { useLanguage } from '../../../../hooks/useLanguage';

const banks = config.get('saAvailableBanks');
const userService = new UserService();

type Props = {
  user: UserType;
};
const mapStateToProps = ({ userData }) => ({
  user: userData && userData.user,
});

const MyProfileBankPage = (props: Props) => {
  const { user } = props;
  const { t } = useTranslation();
  const { country } = useLanguage();
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialUser, setInitialUser] = useState(user);
  const [editedUser, setEditedUser] = useState(user);

  const [isSaving, setSaving] = useState(false);

  const { showSuccessNotification, showErrorNotification } = useNotification();

  // Fetch initial user data
  useEffect(() => {
    (async () => {
      const userData = await userService.getCurrentUser();

      setInitialUser({ ...initialUser, ...userData });
    })();
  }, []);

  useEffect(() => {
    setEditedUser(initialUser);
  }, [initialUser]);

  const handlePhoneChange = (data: PhoneInputData) => {
    setErrors({ ...errors, phone: null });
    const newUser = { ...editedUser };

    newUser.bankAccount.accountNumber = data.raw;
    setEditedUser({
      ...editedUser,
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | any>, bank?, isUserData = false) => {
    const userData = { ...editedUser };

    const inputName = event.target?.name || '';
    const inputValue = event.target?.value || '';

    if (!isUserData) {
      userData.bankAccount[inputName] = inputValue || '';

      if (bank) {
        userData.bankAccount.branchCode = bank?.branchCode || '';
      }
    } else {
      userData[inputName] = inputValue;
    }

    setEditedUser({
      ...editedUser,
      ...userData,
    });
  };

  const openModal = () => {
    setOpen(true);
  };

  const closeModal = (_event?, reason?) => {
    if (reason !== 'backdropClick') {
      setOpen(false);
    }
  };

  const renderSABankOption = () => (
    <div>
      <TextField
        variant="standard"
        id="bank-name"
        value={editedUser?.bankAccount?.bankName?.toString() || ''}
        onClick={() => openModal()}
        margin="normal"
        placeholder=""
        fullWidth={true}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon />
            </InputAdornment>
          ),
        }}
        label={t('pages.settings.profile-bank.bank-name')}
        helperText={
          editedUser?.bankAccount?.bankName && editedUser?.bankAccount?.branchCode
            ? ''
            : t('pages.settings.profile-bank.bank-name-placeholder')
        }
      />
      <Dialog disableEscapeKeyDown open={open} onClose={closeModal} className="bank-selection">
        <DialogTitle>Bank Name:</DialogTitle>
        <DialogContent>
          <hr />
          <FormControl>
            <RadioGroup value={editedUser?.bankAccount?.bankName} name="bankName">
              {banks.map((bank) => (
                <FormControlLabel
                  key={bank.name}
                  control={<Radio color="primary" size="small" />}
                  value={bank.name}
                  label={
                    <>
                      {bank.name}
                      <Typography variant="body2" color="text.secondary">
                        {t('pages.settings.profile-bank.branch-code')} {bank.branchCode}
                      </Typography>
                    </>
                  }
                  onChange={(event) => handleChange(event, bank)}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <hr />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => closeModal(null, null)} color="primary">
            {t('common.ok')}
          </Button>
          <Button variant="text" onClick={() => closeModal(null, null)} color="primary">
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );

  const saveSettings = async () => {
    try {
      // Clear errors
      setErrors({});

      setSaving(true);

      const updatedUser = await userService.updateUserBankAccount(
        {
          bankAccount: editedUser?.bankAccount,
          phone: editedUser?.phone,
          isProfile: true,
        },
        { silentFail: true },
      );

      setUser({ ...user, ...editedUser, ...updatedUser });
      setSaving(false);

      return showSuccessNotification(t('pages.settings.successfully-saved'));
    } catch (error) {
      setSaving(false);

      // Handle API errors
      handleAPIValidationErrors(error.data, setErrors, {});

      return showErrorNotification({
        message: t('pages.settings.saving-error'),
        error,
      });
    }
  };

  return (
    <>
      <Header>
        <HeaderIconLogo icon="back" showLogo={false} />
      </Header>

      <div className="settings-detail-page settings-bank-page mobile-full-width">
        <BlockTitle mt={0} className="settings-title">
          <img src="/icons/icn-bank.svg" className="settings-title-icon" alt="bank" />
          {t('pages.settings.profile-bank.title')}
        </BlockTitle>

        {isSaving ? (
          <Loader />
        ) : (
          <>
            {/* BANK ACCOUNT RECEIVER NAME */}
            <TextField
              variant="standard"
              id="account-holder"
              className="mt-0-i"
              name="accountName"
              value={editedUser?.bankAccount?.accountName}
              error={!!errors?.['bankAccount.accountName']}
              onChange={handleChange}
              margin="normal"
              placeholder={t('pages.settings.profile-bank.name-placeholder')}
              InputLabelProps={{
                shrink: true,
              }}
              label={t('pages.settings.profile-bank.accountholder')}
              helperText={errors?.['bankAccount.accountName']}
            />

            {/* BANK ACCOUNT RECEIVER ACCOUNT NUMBER */}
            {country !== Country.KE && (
              <TextField
                variant="standard"
                id="account-number"
                value={editedUser?.bankAccount?.accountNumber}
                error={!!errors?.['bankAccount.accountNumber']}
                name="accountNumber"
                onChange={handleChange}
                margin="normal"
                placeholder={t('pages.settings.profile-bank.account-placeholder')}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('pages.settings.profile-bank.account-number')}
                helperText={errors?.['bankAccount.accountNumber']}
              />
            )}

            {country === Country.KE && (
              <PhoneInput
                value={editedUser?.bankAccount?.accountNumber || ''}
                onChange={handlePhoneChange}
                error={!!errors?.['bankAccount.accountNumber']}
                helperText={errors?.['bankAccount.accountNumber']}
              />
            )}

            {country === Country.ZA && renderSABankOption()}

            <Button size="large" variant="contained" color="primary" onClick={saveSettings} className="profile-save-btn">
              {t('pages.settings.save-changes')}
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export default withAuth(connect(mapStateToProps)(MyProfileBankPage));
